import type { CommandHandler } from '../types';
import api from '../../../../services/api';

/**
 * Downloads a document generated from a template for the selected rows.
 * Mirrors legacy ZAssets.GridActions.PrintForm / GenerateDocument.
 *
 * Uses the shared axios instance (services/api.ts) so the request goes
 * through the standard auth flow: Authorization header attach, proactive
 * refresh near expiry, and automatic retry-once on 401.
 */
export const printFormHandler: CommandHandler = async (command, context) => {
  const { selectedRows, entityId, entityData } = context;

  const templateId = command.templateSelection;
  if (!templateId) {
    return {
      success: false,
      error: 'Template is not configured for this command',
    };
  }

  // Detail form: always print the opened entity (ignore child-grid selection).
  // Grid list: use selected rows as before.
  const isDetailContext = Boolean(entityData && entityId);
  const entityIds = isDetailContext
    ? [String(entityId)].filter((id) => id && id !== 'undefined' && !id.startsWith('temp_'))
    : Array.from(selectedRows ?? []).filter((id) => id && !id.startsWith('temp_'));

  if (entityIds.length === 0) {
    return {
      success: false,
      error: isDetailContext ? 'Entity ID is not available' : 'Please choose at least one row',
    };
  }

  try {
    const response = await api.post(
      '/umbraco/api/TemplateDocument/GenerateDocumentByTempalte',
      {
        Id: entityIds,
        TemplateId: templateId,
      },
      {
        responseType: 'blob',
      }
    );

    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = resolveFileName(response.headers['content-disposition'], blob.type, entityIds);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: await resolveErrorMessage(err),
    };
  }
};

function resolveFileName(
  contentDisposition: string | undefined,
  contentType: string,
  entityIds: string[]
): string {
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[*]?=(?:"([^"]+)"|([^;]+))/);
    if (match) {
      const rawName = (match[1] || match[2] || '').trim();
      const safeName = rawName.replace(/[/\\:*?"<>|]/g, '-');
      if (safeName) return safeName;
    }
  }

  const dateStr = new Date().toISOString().split('T')[0];
  const extension = getExtensionForContentType(contentType);
  const prefix = entityIds.length > 1 ? 'Archive' : 'Document';
  return extension ? `${prefix}_${dateStr}.${extension}` : `${prefix}_${dateStr}`;
}

function getExtensionForContentType(contentType: string): string | null {
  switch (contentType.toLowerCase()) {
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'docx';
    case 'application/pdf':
      return 'pdf';
    case 'application/zip':
      return 'zip';
    default:
      return null;
  }
}

/**
 * Axios error responses come back as a Blob too (since responseType: 'blob'
 * applies to error responses as well), so JSON error bodies need to be
 * read out of the blob explicitly.
 */
async function resolveErrorMessage(err: unknown): Promise<string> {
  const axiosErr = err as { response?: { data?: Blob }; message?: string };
  const errorBlob = axiosErr?.response?.data;

  if (errorBlob instanceof Blob) {
    try {
      const text = await errorBlob.text();
      const parsed = JSON.parse(text);
      return parsed.Message || parsed.message || text;
    } catch {
      // Not JSON — fall through
    }
  }

  return axiosErr?.message || 'Report generation error';
}
